import { code3FromCode2, countryCodeToLabel } from "@/components/AddressAutoComplete/utils";
import { Address, AddressType } from "@/types";
import { MaterialNames } from "@/components/material_symbol_names";
import { capitalize } from "lodash";

type FormattableAddress = Pick<Address, "address1" | "address2" | "city" | "state" | "postalCode"> & {
  countryCode?: string;
};

function getCountryLabel(countryCode?: string) {
  if (!countryCode) return undefined;

  return countryCodeToLabel(countryCode) || countryCodeToLabel(code3FromCode2(countryCode)) || countryCode;
}

export function formatAddressCopyText(addressLines: ReturnType<typeof formattedAddressLines>, prefix?: string): string {
  return [prefix, addressLines.line1, addressLines.line2, addressLines.line3].filter(Boolean).join(", ");
}

/**
 * Returns the appropriate icon name for a given address type
 * @param addressType - The type of address (home, secondary, main, mailing)
 * @returns The Material Design icon name to display
 */
export function getAddressIconName(addressType: AddressType): MaterialNames {
  switch (addressType) {
    case "home":
      return "home";
    case "secondary":
      return "markunread_mailbox";
    default:
      return "location_on"; // For facility addresses (main/mailing)
  }
}

/**
 * Returns the display label for a given address type
 * @param addressType - The type of address (home, secondary, main, mailing)
 * @param options - Optional configuration for the label
 * @param options.includeFacilityPrefix - Whether to include "Facility" prefix for main/mailing addresses
 * @param options.facilityName - The name of the facility
 * @returns The formatted address type label
 */
export function getAddressTypeLabel(
  addressType: AddressType,
  options?: { includeFacilityPrefix?: boolean; facilityName?: string }
): string {
  const type = addressType === "secondary" ? "Alternate Mailing" : capitalize(addressType);
  const addressLabel = `${type} Address`;

  const isFacilityAddress = ["main", "mailing"].includes(addressType);
  if (isFacilityAddress && options?.includeFacilityPrefix) {
    return options?.facilityName ? `${options.facilityName} ${addressLabel}` : `Facility ${addressLabel}`;
  }

  return addressLabel;
}

export function formattedAddress(address: FormattableAddress) {
  const { address1, address2, city, state, postalCode } = address;
  const addressLine = `${address1}${address2 ? `, ${address2}` : ""}`;
  const cityLine = city;
  const stateAndPostalCode = [state, postalCode].filter(Boolean).join(" ");
  const stateAndPostalCodeSuffix = stateAndPostalCode ? `, ${stateAndPostalCode}` : "";
  const countryLabel = getCountryLabel(address.countryCode);
  const countrySuffix = countryLabel ? `, ${countryLabel}` : "";

  return `${addressLine}, ${cityLine}${stateAndPostalCodeSuffix}${countrySuffix}`;
}

export function formattedAddressLines(address: FormattableAddress) {
  const { address1, address2, city, state, postalCode } = address;
  const stateAndPostalCode = [state, postalCode].filter(Boolean).join(" ");
  const cityWithStateAndPostal = stateAndPostalCode ? `${city}, ${stateAndPostalCode}` : city;
  const countryLabel = getCountryLabel(address.countryCode);

  return {
    line1: `${address1}${address2 ? `, ${address2}` : ""}`,
    line2: cityWithStateAndPostal,
    ...(countryLabel && { line3: countryLabel })
  };
}
